<template>
    <div>
        <tsoft-table
            name="finance-split"
            :key="componentRefreshKey"
            :api="tableApiUrl"
            :items.sync="items"
            :isNeedSearch="true"
            :additionalGetParameter="additionalGetParameter"
            :isNeedCreate="isNeedCreate"
            :typeOfTableFilter="typeOfTableFilter"
            :onDataEmptyMessage="'Разделение счёта отсутствует. Добавьте новый счёт.'"
            @show="showItem"
            @edit="createOrEditItemModal"
            @delete="deleteItem"
        />

        <b-overlay
            :show="dataOverlay"
            spinner-variant="success"
            no-wrap
        ></b-overlay>

        <div class="mt-3 text-right">
            <div>
                <b>Счёт оплачен на сумму:</b> {{ sum_payment | toCurrency }} руб.
            </div>
            <div>
                <b>Сплит счетов на сумму:</b> {{ splitsSum | toCurrency }} руб.
            </div>
        </div>

        <b-sidebar
            v-if="isSidebarOpen"
            v-model="isSidebarOpen"
            id="sidebar-right-create-or-edit-finance-split"
            right
            backdrop
            shadow
            width="80%"
            backdrop-variant="dark"
            ref="editItem"
            no-close-on-backdrop
            @change="closeSidePanelCallback()"
        >
            <template #title>
                <div>
                    <strong id="sidebar-right___title__" v-html="modalTitle()"></strong>
                </div>
            </template>
            <div class="d-block">
                <form class="needs-validation mb-4" novalidate id="financeVirtualForm">
                    <div class="form-row">
                        <div class="form-group col-md-8">
                            <label>Выберите компанию для сплита</label>
                            <tsoft-select-company
                                :key="`select_company_${editedItem.company_id}`"
                                :needNullElement="true"
                                :selected="editedItem.company_id"
                                :selectedCompany="editedItem.company"
                                @set="setCompanyOfSplit"
                          ></tsoft-select-company>
                        </div>
                    </div>

                    <div class="form-row">
                        <div class="form-group col-md-8">
                            <label>Сумма в рублях, которую необходимо зачесть в выбранную компанию</label>
                            <b-form-input
                                required
                                id="sum"
                                type="text"
                                style="max-width: 200px;"
                                v-model="editedItem.sum"
                                @keyup="changeCommasOnDots($event)"
                                @input="checkEnteredSumValidity()"
                            ></b-form-input>
                            <div 
                                v-if="this.calculatedDifference < 0"
                                class="text-danger mt-1"
                            >
                                <small>Сплит-счет превышает допустимую сумму на {{ (this.calculatedDifference * -1) | toCurrency }}</small>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
            <template #footer>
                <div class="row align-items-center">
                    <div class="col d-flex flex-row-reverse">
                        <button
                            class="btn btn-primary text-right" 
                            type="submit" 
                            @click="submitForm($event)"
                        >Сохранить<i class="fas fa-spinner fa-spin ml-1" v-if="savingProcess"></i></button>
                        <button
                            v-if="!savingProcess"
                            class="btn btn-secondary text-right mr-2" 
                            @click="isSidebarOpen=false"
                        >Закрыть</button>
                    </div>
                </div>
            </template>
        </b-sidebar>
    </div>
</template>

<script>
    import {API_FINANCE_SPLITS} from "../../constants"

    import {
        defaultDataItems,
        actionShowItem,
        actionCreateOrUpdateItem,
        actionDeleteItem,
        changeCommasOnDots,
        toCurrency
    } from '../../mixins'

    const initialEditedItem = () => ({
        id: null,
        creator_user_id: null,
		finance_id: null,
        company_id: null,
        sum: 0,
        creator: {},
        finance: {},
        company: {}
    })

    export default {
        components: {
        },
        mixins: [
            defaultDataItems,
            actionShowItem,
            actionCreateOrUpdateItem,
            actionDeleteItem,
            changeCommasOnDots,
        ],
        props: {
            finance_id: { type: Number|String, required: true },
            sum_payment: { type: Number, required: true },
            isNeedCreate: { type: Boolean, required: false, default: true },        // If table needs create new element button
            additionalGetParameter: { type: String, required: false, default: '' }, // If we want to add something, for example, &finance_id=10
            typeOfTableFilter: { type: String, required: false, default: '' },      // Type of filter for output: orders, services
        },
        data() {
            return {
                loading: false,
                tableApiUrl: API_FINANCE_SPLITS,
                componentRefreshKey: 0,
                splitsSum: 0,
                calculatedDifference: 0,
                dataOverlay: false
            }
        },
        watch: {
            items: {
                deep: true,
                handler(finances) {
                    this.splitsSum = 0;

                    if (finances && finances.data) {
                        finances.data.every((finance) => {
                            this.splitsSum = this.splitsSum + finance.sum;

                            return true;
                        })
                    }

                    this.splitsSum = Number(this.splitsSum.toFixed(2));
                },
            }, 
        },
        mounted() {
            this.editedItem = initialEditedItem();
            this.initialAssign();
        },
        methods: {
            initialAssign() {
                this.editedItem.finance_id = this.finance_id;
                this.editedItem.company_id = this.company_id;
            },
            // calls from mixin before sidebaropened
            beforeSidebarOpenedCallback() {
                this.editedItem = initialEditedItem();
                this.initialAssign();
            },
            // calls from mixin on item save
            onCreatedOrUpdatedCallback() {
                this.isSidebarOpen = false
            },
            submitForm(event) {
                event.preventDefault();
                
                let form = document.getElementById("financeVirtualForm");
                let validationError = false;

                // Проверка наличия выбранной компании
                if (!this.editedItem.company_id) {
                    this.$bvToast.toast('Выберите компанию для сплита', {
                        title: 'Ошибка',
                        variant: 'danger',
                        solid: true
                    });
                    validationError = true;
                }

                // Проверка суммы (обязательно и > 0)
                if (!this.editedItem.sum || isNaN(this.editedItem.sum) || Number(this.editedItem.sum) <= 0) {
                    this.$bvToast.toast('Введите корректную сумму для сплита', {
                        title: 'Ошибка',
                        variant: 'danger',
                        solid: true
                    });
                    validationError = true;
                }

                if (validationError) {
                    // form.classList.add('was-validated');
                    return false;
                }

                // HTML5 проверка
                if (form.checkValidity() === false) {
                    // form.classList.add('was-validated');
                    return false;
                }

                // Всё ок — сохраняем
                this.createOrUpdateItem();
            },
            onDeletedCallback() {
                this.componentRefreshKey++
                this.updateParentData()
            },
            checkEnteredSumValidity() {
                this.calculatedDifference = (this.sum_payment - this.splitsSum - this.editedItem.sum).toFixed(2);
            },
            setCompanyOfSplit(value, fullObj) {
                this.editedItem.company_id = value;
                this.editedItem.company = JSON.parse(JSON.stringify(fullObj));
            },
            modalTitle() {
                let title = 'Добавление сплита для счета #' + this.finance_id
                if (this.editIndex > -1) {
                    title = 'Редактирование сплит-счёта #' + this.editedItem.id
                }

                return title
            },
            updateParentData() {
                this.$emit("updateParentData")
            }
        }
    }
</script>
